import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ChevronDown, Copy, Download, Monitor, MoreVertical, Plus, RefreshCw, Search } from 'lucide-react';
import { apiClient } from '../api';
import { useAuth } from '../auth/AuthProvider';
import { APP_DOWNLOAD_URL, APP_REQUIREMENTS, LINUX_APPIMAGE_URL, LINUX_DEB_URL, LINUX_PKGBUILD_URL, LINUX_REQUIREMENTS, useDesktopApp, useLinuxDesktopApp } from '../config/desktopApp';
import ConfirmModal from '../components/UI/ConfirmModal';
import Drawer from '../components/UI/Drawer';
import { parseTs } from '../components/SecurityAlerts/model';
import {
  monoLabel, primaryBtn, primaryBtnStyle, secondaryBtn, secondaryBtnStyle, ghostBtn, ghostBtnStyle,
  inputCls, inputStyle, fieldLabel, fieldLabelStyle, panelStyle, errMessage,
} from '../components/UI/ink';

// Fleet on the left, install guide docked on the right. Every step in the
// guide is taken from start-agent.bat / start-agent.sh — keep them in sync.
// Heartbeats are every 15 min, so "Reporting" allows 30 min before an agent
// turns Late; otherwise healthy agents flicker just before each beat.
const LATE_MIN = 30;
const SILENT_MIN = 60;

const band = (a) => (a.min == null ? 'never' : a.min < LATE_MIN ? 'ok' : a.min < SILENT_MIN ? 'late' : 'silent');
const BAND_LABEL = { ok: 'Reporting', late: 'Late', silent: 'Silent', never: 'Never checked in' };
const BAND_DOT = { ok: 'var(--sev-good-fg)', late: 'var(--sev-med-fg)', silent: 'transparent', never: 'transparent' };

const seenText = (a) => {
  if (a.min == null) return a.created ? `registered ${agoText(a.created)}` : 'registered';
  return agoText(a.last);
};
const agoText = (d) => {
  const m = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.floor(m / 60)} h ago`;
  return `${Math.floor(m / 1440)} d ago`;
};

const seg = (on) => ({
  background: on ? 'var(--surf-raised)' : 'transparent',
  borderColor: on ? 'var(--surf-line)' : 'transparent',
  color: on ? 'var(--ink-text)' : 'var(--ink-muted)',
});

const WINDOWS_BAT = 'https://dragoneye.csnet.my/agent/start-agent.bat';
const CURL = 'curl -sSL https://dragoneye.csnet.my/agent/start-agent.sh -o ~/start-agent.sh\nbash ~/start-agent.sh';

const GUIDES = {
  Windows: {
    needs: ['Python 3.8+ on PATH', 'Admin rights, once', 'Internet access'],
    steps: [
      { title: 'Install Python 3.8 or newer', body: 'From python.org. On the first installer screen, tick “Add python.exe to PATH”, and choose “Install for all users” under Customize installation.' },
      { title: 'Download the launcher', download: true, body: 'Installs to C:\\ProgramData\\DragonEye. Run it again any time to update the agent.' },
      { title: 'Double-click start-agent.bat and approve the admin prompt', body: 'Windows may warn about a downloaded file — choose More info › Run anyway. Admin rights are needed once, to install the background task. Setup continues in a new window.' },
      { title: 'Paste your key, then add a location', body: 'At “Enter your Tenant API Key:”, paste the key above. Then type a location label such as Building-A, or press Enter to skip.' },
      { title: 'Close the window', body: 'The agent now runs in the background as a Windows task and checks in straight away. It starts with the computer and restarts itself if it stops. To see if it’s running, or watch what it’s doing live:', code: 'start-agent.bat --status\nstart-agent.bat --logs' },
    ],
    resetBody: 'Run this in Command Prompt from the folder you downloaded start-agent.bat to. It clears the saved registration and starts setup again, so have the other organisation’s key ready.',
    resetCode: 'start-agent.bat --reset',
  },
  macOS: {
    needs: ['Python 3', 'Homebrew (for SNMP)', 'Your password, once'],
    steps: [
      { title: 'Check Python 3 is installed', body: 'Run python3 --version in Terminal. If it’s missing:', code: 'brew install python3' },
      { title: 'Download and run the setup script', body: 'In Terminal, run:', code: CURL },
      { title: 'Paste your key when asked', body: 'Enter your Mac password first — setup needs admin rights once. At “Enter your Tenant API Key:”, paste the key above. Then add a location label, or press Return to skip.' },
      { title: 'Close Terminal', body: 'The agent now runs in the background (launchd), starts with the Mac and restarts itself if it stops. To see if it’s running, or watch what it’s doing live:', code: 'bash ~/start-agent.sh --status\nbash ~/start-agent.sh --logs' },
    ],
    resetBody: 'Run this in Terminal. It clears the saved registration and starts setup again, so have the other organisation’s key ready.',
    resetCode: 'bash ~/start-agent.sh --reset',
  },
  Linux: {
    needs: ['Debian or Ubuntu with systemd', 'Python 3 + venv', 'curl', 'sudo, once'],
    steps: [
      { title: 'Install the prerequisites', body: 'Package inventory reads dpkg, pip and npm, so RPM-based distros won’t report system packages.', code: 'sudo apt install python3 python3-venv curl' },
      { title: 'Download and run the setup script', body: 'As your normal user (not root):', code: CURL },
      { title: 'Paste your key when asked', body: 'Enter your sudo password first — setup needs root once. At “Enter your Tenant API Key:”, paste the key above. Then add a location label, or press Enter to skip.' },
      { title: 'Close the terminal', body: 'The agent now runs as the systemd service dragoneye-agent, starts at boot and restarts itself if it stops. Closing the terminal or SSH session doesn’t affect it. To see if it’s running, or watch what it’s doing live:', code: 'bash ~/start-agent.sh --status\nbash ~/start-agent.sh --logs' },
    ],
    resetBody: 'Run this on the machine. It clears the saved registration and starts setup again, so have the other organisation’s key ready.',
    resetCode: 'bash ~/start-agent.sh --reset',
  },
};

const guessOs = () => {
  const p = (navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || '').toLowerCase();
  if (p.includes('mac')) return 'macOS';
  if (p.includes('linux') || p.includes('x11')) return 'Linux';
  return 'Windows';
};

// Copies text and flips `key` to "copied" for two seconds.
const useCopy = () => {
  const [copied, setCopied] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(null), 2000);
    } catch { /* clipboard blocked */ }
  };
  return [copied, copy];
};

const RowMenu = ({ agent, canDelete, onView, onDelete }) => {
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const open = !!pos;

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setPos(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setPos(null); };
    const onScroll = () => setPos(null);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const toggle = () => {
    if (open) { setPos(null); return; }
    const r = btnRef.current.getBoundingClientRect();
    const up = window.innerHeight - r.bottom < 120;
    setPos({ right: window.innerWidth - r.right, ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
  };

  const actions = [
    { label: 'View asset', run: onView },
    canDelete && { label: 'Delete agent', run: onDelete, danger: true },
  ].filter(Boolean);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`More actions for ${agent.host}`}
        className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-transparent hover:border-[color:var(--surf-line)]"
        style={{ color: 'var(--ink-faint)' }}
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && createPortal(
        <div ref={menuRef} role="menu" className="fixed z-40 min-w-[180px] p-1 rounded-[10px] border text-left" style={{ ...pos, background: 'var(--surf-surface)', borderColor: 'var(--surf-line-strong)', boxShadow: '0 12px 32px rgba(0,0,0,0.3)' }}>
          {actions.map(a => (
            <button
              key={a.label}
              type="button"
              role="menuitem"
              onClick={() => { setPos(null); a.run(); }}
              className="w-full h-10 px-3 rounded-md text-left text-[13px] hover:bg-[color:var(--surf-raised)]"
              style={{ color: a.danger ? 'var(--sev-crit-fg)' : 'var(--ink-text)' }}
            >
              {a.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};

const EMPTY_FORM = { hostname: '', os_type: 'Linux', os_info: '', ip_address: '' };

const RegisterDrawer = ({ open, onClose }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, copy] = useCopy();

  useEffect(() => {
    if (open) { setForm(EMPTY_FORM); setError(''); setResult(null); }
  }, [open]);

  const field = (k) => ({ value: form[k], onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await apiClient.post('/api/register', form);
      setResult(res.data);
    } catch (err) {
      setError(errMessage(err, err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={open}
      kicker="AGENT REGISTRY"
      title={result ? 'Agent registered' : 'Register manually'}
      subtitle={result ? undefined : 'For machines where you’ll configure the agent yourself. The launcher in the install guide does this automatically.'}
      onClose={() => onClose(!!result)}
      footer={result ? (
        <>
          <span />
          <button type="button" onClick={() => onClose(true)} className={primaryBtn} style={primaryBtnStyle}>Done</button>
        </>
      ) : (
        <>
          <button type="button" onClick={() => onClose(false)} className={ghostBtn} style={ghostBtnStyle}>Cancel</button>
          <button type="submit" form="register-agent" disabled={busy || !form.hostname.trim()} className={primaryBtn} style={primaryBtnStyle}>
            {busy ? 'Registering…' : 'Register'}
          </button>
        </>
      )}
    >
      {result ? (
        <div className="flex flex-col gap-4">
          <p className="m-0 text-sm" style={{ color: 'var(--ink-muted)' }}>
            <span className="font-mono" style={{ color: 'var(--ink-text)' }}>{result.hostname}</span> is registered. This key is shown once — copy it now.
          </p>
          <div className="flex items-center gap-2.5 p-3 rounded-[10px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
            <span className="flex flex-col gap-1 flex-grow min-w-0">
              <span className={monoLabel} style={{ color: 'var(--ink-faint)' }}>AGENT API KEY</span>
              <code className="font-mono text-[13px] break-all">{result.api_key}</code>
            </span>
            <button type="button" onClick={() => copy('key', result.api_key)} className="h-[34px] px-3 rounded-lg text-[12.5px] font-semibold shrink-0" style={primaryBtnStyle}>
              {copied === 'key' ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px]" style={{ color: 'var(--ink-muted)' }}>On the target machine, add to <code className="font-mono">.env</code>:</span>
            <code className="block p-3 rounded-lg border font-mono text-[12px] break-all" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>API_KEY={result.api_key}</code>
          </div>
        </div>
      ) : (
        <form id="register-agent" onSubmit={submit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className={fieldLabel} style={fieldLabelStyle}>Hostname</span>
            <input {...field('hostname')} required placeholder="web-server-01" className={inputCls} style={inputStyle} />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className={fieldLabel} style={fieldLabelStyle}>OS type</span>
            <select {...field('os_type')} className={inputCls} style={inputStyle}>
              {['Linux', 'Windows', 'macOS', 'Other'].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className={fieldLabel} style={fieldLabelStyle}>OS version <span style={{ color: 'var(--ink-faint)' }}>(optional)</span></span>
            <input {...field('os_info')} placeholder="Ubuntu 24.04" className={inputCls} style={inputStyle} />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className={fieldLabel} style={fieldLabelStyle}>IP address <span style={{ color: 'var(--ink-faint)' }}>(optional)</span></span>
            <input {...field('ip_address')} placeholder="10.0.1.10" className={inputCls} style={inputStyle} />
          </label>
          {error && (
            <p className="m-0 p-3 rounded-lg text-[13px]" style={{ background: 'var(--sev-crit-bg)', color: 'var(--sev-crit-fg)' }}>{error}</p>
          )}
        </form>
      )}
    </Drawer>
  );
};

const AgentRegistry = () => {
  const navigate = useNavigate();
  const { permissions: myPerms } = useAuth();
  const can = useCallback(p => (myPerms || []).includes(p), [myPerms]);

  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [os, setOs] = useState(guessOs);
  const [tenantKey, setTenantKey] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [, setTick] = useState(0);
  const [copied, copy] = useCopy();
  const { available: appAvailable, info: appInfo } = useDesktopApp();
  const { available: linuxAvailable, info: linuxInfo } = useLinuxDesktopApp();

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/servers?state=REGISTERED');
      setServers(res.data.servers || []);
      setLoadError(null);
    } catch (err) {
      setLoadError(errMessage(err, 'Could not load agents.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
    const id = setInterval(fetchAgents, 30000);
    return () => clearInterval(id);
  }, [fetchAgents]);

  // Re-render every minute so "N min ago" and the status bands stay current.
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    apiClient.get('/api/tenant/api-key')
      .then(res => setTenantKey(res.data.api_key || null))
      .catch(() => setTenantKey(null));
  }, []);

  const agents = servers
    .filter(s => s.source !== 'arp_passive' && s.lifecycle_state !== 'DISCOVERED')
    .map(s => {
      const last = parseTs(s.last_seen);
      return {
        id: s.id,
        host: s.hostname || s.id,
        ip: s.ip_address,
        os: s.os_type || '—',
        osv: s.os_info || '',
        last,
        created: parseTs(s.created_at),
        min: last ? Math.max(0, (Date.now() - last.getTime()) / 60000) : null,
      };
    });

  const counts = { ok: 0, late: 0, silent: 0, never: 0 };
  agents.forEach(a => { counts[band(a)] += 1; });
  const tabs = [
    ['all', 'All', agents.length],
    ['ok', 'Reporting', counts.ok],
    ['late', 'Late', counts.late],
    ...(counts.silent ? [['silent', 'Silent', counts.silent]] : []),
    ['never', 'Never checked in', counts.never],
  ];
  const activeFilter = filter === 'silent' && !counts.silent ? 'all' : filter;
  const q = search.trim().toLowerCase();
  const rows = agents
    .filter(a => activeFilter === 'all' || band(a) === activeFilter)
    .filter(a => !q || a.host.toLowerCase().includes(q) || (a.ip || '').includes(q))
    .sort((a, b) => (a.min ?? Infinity) - (b.min ?? Infinity));

  const empty = !loading && !loadError && agents.length === 0;
  const summary = loading && !servers.length
    ? 'Loading…'
    : empty
      ? 'No agents registered yet'
      : [
          `${agents.length} agent${agents.length === 1 ? '' : 's'}`,
          `${counts.ok} reporting`,
          counts.late && `${counts.late} late`,
          counts.silent && `${counts.silent} silent`,
          counts.never && `${counts.never} waiting for first check-in`,
        ].filter(Boolean).join(' · ');

  const G = GUIDES[os];
  const keyText = !tenantKey
    ? 'Unavailable — ask an admin'
    : showKey ? tenantKey : `••••••••••••••••••••  ${tenantKey.slice(-4)}`;

  const doDelete = async () => {
    const a = confirmDelete;
    setConfirmDelete(null);
    try {
      await apiClient.delete(`/api/servers/${a.id}`);
      fetchAgents();
    } catch (err) {
      setLoadError(errMessage(err, `Could not delete ${a.host}.`));
    }
  };

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-[18px]" style={{ color: 'var(--ink-text)' }}>
      {/* Title row */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <span className="font-mono text-[11.5px] tracking-[0.14em]" style={{ color: 'var(--ink-link)' }}>ASSETS &amp; DEFENSE</span>
          <h1 className="m-0 font-heading text-[30px] font-semibold tracking-[-0.01em] leading-tight">Agent Registry</h1>
          <p className="m-0 text-sm" style={{ color: 'var(--ink-muted)' }}>{summary}</p>
        </div>
        <div className="flex items-center gap-2.5">
          <button type="button" onClick={fetchAgents} disabled={loading} className={secondaryBtn} style={secondaryBtnStyle}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.8} />Refresh
          </button>
          {can('assets:create') && (
            <button type="button" onClick={() => setRegisterOpen(true)} className={secondaryBtn} style={secondaryBtnStyle}>
              <Plus className="w-4 h-4" strokeWidth={1.8} />Register manually
            </button>
          )}
        </div>
      </div>

      {loadError && (
        <p role="alert" className="m-0 px-4 py-3 rounded-[10px] text-[13px]" style={{ background: 'var(--sev-crit-bg)', color: 'var(--sev-crit-fg)' }}>{loadError}</p>
      )}

      <div className="grid gap-5 grid-cols-1 xl:grid-cols-[minmax(0,1fr)_480px] items-start">
        {/* Fleet */}
        <section aria-label="Agents" className="rounded-[14px] border flex flex-col overflow-hidden min-h-[520px]" style={panelStyle}>
          {empty ? (
            <div
              className="flex-grow flex flex-col items-center justify-center gap-3.5 p-12 text-center"
              style={{ backgroundImage: 'radial-gradient(var(--surf-raised) 1px, transparent 1px)', backgroundSize: '22px 22px' }}
            >
              <span className="w-14 h-14 rounded-[14px] border border-dashed flex items-center justify-center" style={{ borderColor: 'var(--surf-line-strong)', background: 'var(--surf-surface)' }}>
                <Monitor className="w-6 h-6" strokeWidth={1.6} style={{ color: 'var(--ink-muted)' }} />
              </span>
              <span className="font-heading text-[22px] font-semibold">No agents yet</span>
              <span className="text-sm leading-relaxed max-w-[420px]" style={{ color: 'var(--ink-muted)' }}>
                Install the agent on your first server or laptop using the guide{' '}
                <span className="hidden xl:inline">on the right</span><span className="xl:hidden">below</span>.
                It checks in as soon as it starts, and appears here within a minute.
              </span>
              <span className="flex items-center gap-2 h-[34px] px-3.5 rounded-full border text-[13px]" style={{ borderColor: 'var(--surf-line)', background: 'var(--surf-surface)', color: 'var(--ink-muted)' }}>
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--accent)' }} />Listening for check-ins…
              </span>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2.5 px-4 py-3.5 border-b" style={{ borderColor: 'var(--surf-line)' }}>
                <div className="w-[200px] h-[38px] flex items-center gap-2 px-3 rounded-[10px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
                  <Search className="w-3.5 h-3.5 shrink-0" strokeWidth={1.8} style={{ color: 'var(--ink-faint)' }} />
                  <input
                    type="search"
                    aria-label="Filter agents"
                    placeholder="Hostname or IP"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="flex-grow min-w-0 h-9 bg-transparent border-0 outline-none text-[13px]"
                    style={{ color: 'var(--ink-text)' }}
                  />
                </div>
                <div role="group" aria-label="Status" className="flex flex-wrap gap-[3px] p-[3px] rounded-[10px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
                  {tabs.map(([id, label, n]) => (
                    <button key={id} type="button" onClick={() => setFilter(id)} aria-pressed={activeFilter === id} className="h-[30px] flex items-center gap-1.5 px-2.5 rounded-[7px] border text-[12.5px]" style={seg(activeFilter === id)}>
                      {label}<span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{n}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] border-collapse table-fixed">
                  <thead>
                    <tr className="h-10 border-b" style={{ borderColor: 'var(--surf-line)' }}>
                      <th scope="col" className={`${monoLabel} font-medium text-left px-4`} style={{ color: 'var(--ink-faint)' }}>AGENT</th>
                      <th scope="col" className={`${monoLabel} font-medium text-left px-2 w-32`} style={{ color: 'var(--ink-faint)' }}>OS</th>
                      <th scope="col" className={`${monoLabel} font-medium text-left px-2 w-[168px]`} style={{ color: 'var(--ink-faint)' }}>LAST CHECK-IN</th>
                      <th scope="col" className="w-[52px] pr-3"><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(a => {
                      const b = band(a);
                      return (
                        <tr key={a.id} className="h-[58px] border-b hover:bg-[color:var(--surf-raised)]" style={{ borderColor: 'var(--surf-line)' }}>
                          <td className="px-4">
                            <Link to={`/assets/${a.id}`} className="flex flex-col gap-[3px] min-w-0" style={{ color: 'var(--ink-text)' }}>
                              <span className="font-mono text-[13px] truncate">{a.host}</span>
                              <span className="font-mono text-[11px] truncate" style={{ color: 'var(--ink-faint)' }}>{a.id.slice(0, 8)}{a.ip ? ` · ${a.ip}` : ''}</span>
                            </Link>
                          </td>
                          <td className="px-2">
                            <span className="flex flex-col gap-0.5 min-w-0">
                              <span className="text-[13px] truncate">{a.os}</span>
                              {a.osv && <span className="text-[11.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{a.osv}</span>}
                            </span>
                          </td>
                          <td className="px-2">
                            <span className="flex flex-col gap-[3px]">
                              <span className="flex items-center gap-[7px] text-[13px]">
                                <span className="w-2 h-2 rounded-full box-border shrink-0" style={{ background: BAND_DOT[b], border: `1px ${b === 'never' ? 'dashed' : 'solid'} ${BAND_DOT[b] === 'transparent' ? 'var(--ink-faint)' : BAND_DOT[b]}` }} />
                                {BAND_LABEL[b]}
                              </span>
                              <span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{seenText(a)}</span>
                            </span>
                          </td>
                          <td className="pr-3 text-right">
                            <RowMenu agent={a} canDelete={can('assets:delete')} onView={() => navigate(`/assets/${a.id}`)} onDelete={() => setConfirmDelete(a)} />
                          </td>
                        </tr>
                      );
                    })}
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-12 text-center text-[13px]" style={{ color: 'var(--ink-faint)' }}>
                          {loading && !servers.length ? 'Loading agents…' : 'No agents match these filters.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-auto px-4 py-3 border-t text-xs" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-faint)' }}>
                Reporting = checked in within {LATE_MIN} min (heartbeat every 15 min) · Late = {LATE_MIN}–{SILENT_MIN} min · Silent = over an hour
              </div>
            </>
          )}
        </section>

        {/* Install guide */}
        <aside aria-label="Install an agent" className="rounded-[14px] border flex flex-col overflow-hidden xl:sticky xl:top-6" style={panelStyle}>
          <div className="flex flex-col gap-1.5 px-[22px] pt-[18px] pb-3.5">
            <span className={monoLabel} style={{ color: 'var(--ink-link)' }}>QUICK START</span>
            <h2 className="m-0 font-heading text-xl font-semibold tracking-[-0.01em]">Install the agent on a new machine</h2>
            <p className="m-0 text-[13px] leading-normal" style={{ color: 'var(--ink-muted)' }}>One launcher installs the Python packages, registers the machine with your organisation and starts the agent.</p>
          </div>

          <div className="px-[22px] pb-3">
            {(appAvailable || linuxAvailable) && (
              <>
                {appAvailable && (
                <div id="get-the-app" className="flex flex-col gap-3 p-4 rounded-xl border" style={{ borderColor: 'var(--accent)', background: 'color-mix(in srgb, var(--accent) 10%, transparent)' }}>
                  <div className="flex items-center gap-2.5">
                    <span className="h-[18px] px-1.5 rounded-[9px] flex items-center font-mono text-[9.5px] tracking-[0.06em]" style={{ background: 'var(--accent)', color: '#0A0A0A' }}>NEW</span>
                    <span className="text-sm font-bold">DragonEye app for Windows</span>
                  </div>
                  <p className="m-0 text-[13px] leading-normal" style={{ color: 'var(--ink-muted)' }}>The simplest way on Windows. Sign in, paste your tenant key, and the app installs the agent. It also shows you, live, what the agent is doing on that computer.</p>
                  <div className="flex flex-wrap items-center gap-3">
                    <a href={APP_DOWNLOAD_URL} download className="h-10 inline-flex items-center gap-2 px-[18px] rounded-lg text-sm font-semibold" style={primaryBtnStyle}>
                      <Download className="w-4 h-4" strokeWidth={2} />Download the app
                    </a>
                    <span className="font-mono text-[11px] tracking-[0.04em]" style={{ color: 'var(--ink-faint)' }}>
                      {[APP_REQUIREMENTS, appInfo && `v${appInfo.version}`, appInfo && appInfo.size].filter(Boolean).join(' · ').toUpperCase()}
                    </span>
                  </div>
                  <p className="m-0 text-xs leading-normal" style={{ color: 'var(--ink-faint)' }}>
                    Windows may show a blue &ldquo;Windows protected your PC&rdquo; screen, because the app is not code-signed yet. Choose <strong>More info</strong>, then <strong>Run anyway</strong>.
                  </p>
                  {appInfo && (
                    <details className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                      <summary className="cursor-pointer">What&apos;s new in v{appInfo.version}{appInfo.released ? ` (${appInfo.released})` : ''} and how to verify the download</summary>
                      {appInfo.notes.length > 0 && (
                        <ul className="mt-2 mb-0 pl-4 flex flex-col gap-1">
                          {appInfo.notes.map((n) => <li key={n}>{n}</li>)}
                        </ul>
                      )}
                      <p className="mt-2 mb-0 font-mono text-[11px] break-all" style={{ color: 'var(--ink-faint)' }}>SHA-256 {appInfo.sha256}</p>
                    </details>
                  )}
                </div>
                )}
                {linuxAvailable && (
                  <div id="get-the-app-linux" className={`flex flex-col gap-3 p-4 rounded-xl border${appAvailable ? ' mt-3' : ''}`} style={{ borderColor: 'var(--accent)', background: 'color-mix(in srgb, var(--accent) 10%, transparent)' }}>
                    <div className="flex items-center gap-2.5">
                      <span className="h-[18px] px-1.5 rounded-[9px] flex items-center font-mono text-[9.5px] tracking-[0.06em]" style={{ background: 'var(--accent)', color: '#0A0A0A' }}>NEW</span>
                      <span className="text-sm font-bold">DragonEye app for Linux</span>
                    </div>
                    <p className="m-0 text-[13px] leading-normal" style={{ color: 'var(--ink-muted)' }}>Sign in, paste your tenant key, and the app installs the agent. Use the AppImage on any distribution, the .deb on Debian and Ubuntu, or the PKGBUILD on Arch.</p>
                    <div className="flex flex-wrap items-center gap-3">
                      <a href={LINUX_APPIMAGE_URL} download className="h-10 inline-flex items-center gap-2 px-[18px] rounded-lg text-sm font-semibold" style={primaryBtnStyle}>
                        <Download className="w-4 h-4" strokeWidth={2} />Download AppImage
                      </a>
                      {LINUX_DEB_URL && (
                        <a href={LINUX_DEB_URL} download className="h-10 inline-flex items-center gap-2 px-[18px] rounded-lg border text-sm font-semibold" style={{ borderColor: 'var(--accent)', color: 'var(--ink-link)' }}>
                          <Download className="w-4 h-4" strokeWidth={2} />Download .deb
                        </a>
                      )}
                      <span className="font-mono text-[11px] tracking-[0.04em]" style={{ color: 'var(--ink-faint)' }}>
                        {[LINUX_REQUIREMENTS, linuxInfo && `v${linuxInfo.version}`].filter(Boolean).join(' · ').toUpperCase()}
                      </span>
                    </div>
                    <p className="m-0 text-xs leading-normal" style={{ color: 'var(--ink-faint)' }}>
                      For the AppImage, run <code>chmod +x UM-DragonEye-Linux.AppImage</code> first, then start it. The .deb installs with <code>sudo apt install ./UM-DragonEye-Linux.deb</code>.
                    </p>
                    {LINUX_DEB_URL && (
                      <div className="flex flex-col gap-1.5">
                        <p className="m-0 text-xs leading-normal" style={{ color: 'var(--ink-faint)' }}>
                          On Arch Linux, install it with pacman instead (no AppImage or FUSE needed):
                        </p>
                        <pre className="m-0 p-2.5 rounded-lg border overflow-x-auto font-mono text-[11.5px] leading-relaxed" style={{ borderColor: 'var(--surf-line)', background: 'var(--surf-bg)', color: 'var(--ink-muted)' }}>
{`curl -O ${new URL(LINUX_PKGBUILD_URL, window.location.origin).href}\nmakepkg -si`}
                        </pre>
                      </div>
                    )}
                    {linuxInfo && (
                      <details className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                        <summary className="cursor-pointer">What&apos;s new in v{linuxInfo.version}{linuxInfo.released ? ` (${linuxInfo.released})` : ''} and how to verify the download</summary>
                        {linuxInfo.notes.length > 0 && (
                          <ul className="mt-2 mb-0 pl-4 flex flex-col gap-1">
                            {linuxInfo.notes.map((n) => <li key={n}>{n}</li>)}
                          </ul>
                        )}
                        {linuxInfo.appimage && <p className="mt-2 mb-0 font-mono text-[11px] break-all" style={{ color: 'var(--ink-faint)' }}>AppImage ({linuxInfo.appimage.size}) SHA-256 {linuxInfo.appimage.sha256}</p>}
                        {linuxInfo.deb && <p className="mt-2 mb-0 font-mono text-[11px] break-all" style={{ color: 'var(--ink-faint)' }}>.deb ({linuxInfo.deb.size}) SHA-256 {linuxInfo.deb.sha256}</p>}
                      </details>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-3 mt-4 font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }} role="separator">
                  <span className="flex-grow h-px" style={{ background: 'var(--surf-line)' }} />OR INSTALL MANUALLY<span className="flex-grow h-px" style={{ background: 'var(--surf-line)' }} />
                </div>
              </>
            )}
          </div>

          <div className="px-[22px] pb-3">
            <div role="tablist" aria-label="Operating system" className="grid grid-cols-3 gap-1 p-1 rounded-[10px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
              {Object.keys(GUIDES).map(o => (
                <button key={o} type="button" role="tab" aria-selected={os === o} onClick={() => setOs(o)} className="h-9 rounded-[7px] border text-[13.5px] font-medium" style={seg(os === o)}>
                  {o}
                </button>
              ))}
            </div>
          </div>

          <div role="tabpanel" aria-label={`${os} install steps`} className="px-[22px] flex flex-col gap-3">
            <div className="flex flex-wrap gap-1.5">
              {G.needs.map(t => (
                <span key={t} className="h-6 inline-flex items-center px-[9px] rounded-md border text-xs" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>{t}</span>
              ))}
            </div>

            <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
              <span className="flex flex-col gap-[3px] flex-grow min-w-0">
                <span className="font-mono text-[10px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>YOUR TENANT API KEY</span>
                <span className="font-mono text-[13px] truncate">{keyText}</span>
              </span>
              {tenantKey && (
                <>
                  <button type="button" onClick={() => setShowKey(v => !v)} className="h-[34px] px-2.5 rounded-lg border text-[12.5px] shrink-0" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
                    {showKey ? 'Hide' : 'Show'}
                  </button>
                  <button type="button" onClick={() => copy('tenant', tenantKey)} className="h-[34px] px-3 rounded-lg text-[12.5px] font-semibold shrink-0" style={primaryBtnStyle}>
                    {copied === 'tenant' ? 'Copied' : 'Copy'}
                  </button>
                </>
              )}
            </div>

            <ol className="m-0 p-0 list-none flex flex-col">
              {G.steps.map((st, i) => (
                <li key={st.title} className="flex gap-3 py-2.5 border-t" style={{ borderColor: 'var(--surf-line)' }}>
                  <span className="w-6 h-6 shrink-0 rounded-full border flex items-center justify-center font-mono text-[11.5px]" style={{ borderColor: 'var(--surf-line-strong)', color: 'var(--ink-muted)' }}>{i + 1}</span>
                  <span className="flex-grow min-w-0 flex flex-col gap-1.5">
                    <span className="text-sm font-semibold">{st.title}</span>
                    {st.body && <span className="text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{st.body}</span>}
                    {st.download && (
                      <a href={WINDOWS_BAT} download className="self-start h-[38px] inline-flex items-center gap-2 px-3.5 rounded-[9px] text-[13.5px] font-semibold" style={primaryBtnStyle}>
                        <Download className="w-[15px] h-[15px]" strokeWidth={2} />Download start-agent.bat
                      </a>
                    )}
                    {st.code && (
                      <span className="relative block py-2.5 pl-3 pr-11 rounded-lg border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
                        <code className="block font-mono text-[11.5px] leading-relaxed whitespace-pre-wrap break-all">{st.code}</code>
                        <button
                          type="button"
                          onClick={() => copy(`${os}${i}`, st.code)}
                          aria-label={copied === `${os}${i}` ? 'Copied' : 'Copy command'}
                          className="absolute top-1.5 right-1.5 w-[30px] h-[30px] flex items-center justify-center rounded-[7px] border"
                          style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)', color: copied === `${os}${i}` ? 'var(--sev-good-fg)' : 'var(--ink-muted)' }}
                        >
                          {copied === `${os}${i}` ? <Check className="w-[13px] h-[13px]" /> : <Copy className="w-[13px] h-[13px]" strokeWidth={1.8} />}
                        </button>
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-2 border-t" style={{ borderColor: 'var(--surf-line)' }}>
            <button
              type="button"
              onClick={() => setResetOpen(v => !v)}
              aria-expanded={resetOpen}
              className="w-full h-[46px] flex items-center justify-between px-[22px] text-[13.5px] font-medium"
              style={{ color: 'var(--ink-text)' }}
            >
              <span>Move a machine to another organisation</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${resetOpen ? 'rotate-180' : ''}`} strokeWidth={1.8} style={{ color: 'var(--ink-faint)' }} />
            </button>
            {resetOpen && (
              <div className="px-[22px] pb-4 flex flex-col gap-2">
                <span className="text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{G.resetBody}</span>
                <span className="relative block py-2.5 pl-3 pr-11 rounded-lg border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
                  <code className="block font-mono text-[11.5px] whitespace-pre-wrap break-all">{G.resetCode}</code>
                  <button
                    type="button"
                    onClick={() => copy(`${os}-reset`, G.resetCode)}
                    aria-label={copied === `${os}-reset` ? 'Copied' : 'Copy command'}
                    className="absolute top-1.5 right-1.5 w-[30px] h-[30px] flex items-center justify-center rounded-[7px] border"
                    style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)', color: copied === `${os}-reset` ? 'var(--sev-good-fg)' : 'var(--ink-muted)' }}
                  >
                    {copied === `${os}-reset` ? <Check className="w-[13px] h-[13px]" /> : <Copy className="w-[13px] h-[13px]" strokeWidth={1.8} />}
                  </button>
                </span>
              </div>
            )}
          </div>
        </aside>
      </div>

      <RegisterDrawer
        open={registerOpen}
        onClose={(changed) => { setRegisterOpen(false); if (changed) fetchAgents(); }}
      />
      <ConfirmModal
        isOpen={!!confirmDelete}
        danger
        title={`Delete ${confirmDelete?.host}?`}
        message="The machine is removed from the registry along with its snapshots and alerts. If the agent is still running it will fail to check in until it’s registered again."
        confirmLabel="Delete agent"
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
};

export default AgentRegistry;
